// demo.ts — zod schemas for the public playground's unauthenticated routes (docs/superpowers/
// specs/2026-08-12-public-playground-design.md, Task 4). `apps/api/src/routes/demo.ts` is the
// only consumer.
import { z } from 'zod';
import { httpUrl } from './sources.ts';

/** `POST /v1/demo` body. `turnstileToken` is the widget's `cf-turnstile-response` value —
 * bounded at 2048 chars per Cloudflare's own Worker-side example (`/turnstile/spin/`), a
 * generous ceiling for a token that is never actually that long in practice. */
export const createDemoSchema = z.object({
  url: httpUrl,
  turnstileToken: z.string().min(1).max(2048),
});
export type CreateDemoInput = z.infer<typeof createDemoSchema>;

/** One turn of the CLIENT-SUPPLIED conversation history a demo chat carries (task-4 brief
 * decision 4 — a demo cannot write `Conversation`/`Message` rows, so there is nothing
 * server-side to reload prior turns from; the browser re-sends them every request). Bounded
 * to the same 2000-char ceiling `publicChatSchema`/`playgroundChatSchema` already use for a
 * single message. */
export const demoHistoryMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(2000),
});

/**
 * `POST /v1/demo/:publicId/chat` body. `history` is capped at 40 entries here — generous
 * enough for a real conversation, small enough that the request body itself can't be turned
 * into an amplification vector — the ROUTE separately trims whatever arrives down to the
 * last 6 turns before it ever reaches the model (decision 4's "trimmed server-side to the
 * last 6"); this schema-level cap exists only to bound the payload itself, not to encode the
 * 6-turn window (that number lives in exactly one place, the route).
 *
 * Why a client-supplied, unauthenticated history is safe to trust at all (decision 4's own
 * required justification): it is the visitor's OWN conversation with a bot that answers only
 * from that bot's indexed content — there is no second party's data to protect and no
 * privileged instruction a fabricated history could smuggle in that the visitor couldn't
 * already just type in `message` itself. A stranger who wants to put words in the bot's mouth
 * can already do that by typing them.
 */
export const demoChatSchema = z.object({
  message: z.string().min(1).max(2000),
  history: z.array(demoHistoryMessageSchema).max(40).default([]),
});
export type DemoChatInput = z.infer<typeof demoChatSchema>;

/** `POST /api/me/demo-ref` body. Deliberately a loose `string`, NOT
 * `DEMO_PUBLIC_ID_PATTERN` — an id that doesn't resolve must come back 200
 * `{ recorded: false }` (spec §B: "never 4xx on an unknown id"), so shape enforcement
 * here would turn a stale/garbage id into the one 4xx the contract forbids. */
export const demoRefSchema = z.object({ demoId: z.string().min(1).max(64) });
export type DemoRefInput = z.infer<typeof demoRefSchema>;
